import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

export type AlertType = 
  | "default" 
  | "info" 
  | "success" 
  | "warning" 
  | "error" 
  | "critical";

interface MacOSAlertProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type: AlertType;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  autoClose?: number; // time in ms, if not provided or 0, alert won't auto close
  icon?: React.ReactNode;
  showProgress?: boolean;
}

const alertTypeStyles = {
  default: {
    bg: "bg-gradient-to-br from-gray-50 to-gray-100",
    border: "border-gray-200",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-blue-500">
        <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
      </svg>
    ),
    confirmBtn: "bg-blue-500 hover:bg-blue-600 focus:ring-blue-300 text-white",
    cancelBtn: "bg-gray-100 hover:bg-gray-200 focus:ring-gray-200 text-gray-800"
  },
  info: {
    bg: "bg-gradient-to-br from-blue-50 to-blue-100",
    border: "border-blue-200",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-blue-500">
        <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
      </svg>
    ),
    confirmBtn: "bg-blue-500 hover:bg-blue-600 focus:ring-blue-300 text-white",
    cancelBtn: "bg-blue-50 hover:bg-blue-100 focus:ring-blue-200 text-blue-800"
  },
  success: {
    bg: "bg-gradient-to-br from-green-50 to-green-100",
    border: "border-green-200",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-green-500">
        <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
      </svg>
    ),
    confirmBtn: "bg-green-500 hover:bg-green-600 focus:ring-green-300 text-white",
    cancelBtn: "bg-green-50 hover:bg-green-100 focus:ring-green-200 text-green-800"
  },
  warning: {
    bg: "bg-gradient-to-br from-amber-50 to-amber-100",
    border: "border-amber-200",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-amber-500">
        <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
      </svg>
    ),
    confirmBtn: "bg-amber-500 hover:bg-amber-600 focus:ring-amber-300 text-white",
    cancelBtn: "bg-amber-50 hover:bg-amber-100 focus:ring-amber-200 text-amber-800"
  },
  error: {
    bg: "bg-gradient-to-br from-red-50 to-red-100",
    border: "border-red-200",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-red-500">
        <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
      </svg>
    ),
    confirmBtn: "bg-red-500 hover:bg-red-600 focus:ring-red-300 text-white",
    cancelBtn: "bg-red-50 hover:bg-red-100 focus:ring-red-200 text-red-800"
  },
  critical: {
    bg: "bg-gradient-to-br from-rose-50 to-rose-100",
    border: "border-rose-200",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-rose-500">
        <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
      </svg>
    ),
    confirmBtn: "bg-rose-500 hover:bg-rose-600 focus:ring-rose-300 text-white",
    cancelBtn: "bg-rose-50 hover:bg-rose-100 focus:ring-rose-200 text-rose-800"
  }
};

const MacOSAlert: React.FC<MacOSAlertProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type,
  confirmText = "موافق",
  cancelText = "إلغاء",
  onConfirm,
  onCancel,
  autoClose = 0,
  icon,
  showProgress = true
}) => {
  const [progress, setProgress] = useState(100);
  const styles = alertTypeStyles[type];
  const customIcon = icon || styles.icon;
  
  useEffect(() => {
    if (!isOpen) return;
    
    let interval: number | null = null;
    
    if (autoClose && autoClose > 0) {
      const startTime = Date.now();
      const endTime = startTime + autoClose;
      
      interval = window.setInterval(() => {
        const now = Date.now();
        const remaining = endTime - now;
        const percentRemaining = Math.max(0, (remaining / autoClose) * 100);
        
        setProgress(percentRemaining);
        
        if (now >= endTime) {
          clearInterval(interval as number);
          onClose();
        }
      }, 16); // ~60fps update
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isOpen, autoClose, onClose]);
  
  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    onClose();
  };
  
  const handleCancel = () => {
    if (onCancel) onCancel();
    onClose();
  };
  
  // Render nothing if there's no document (SSR)
  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel || onClose}
          />
          
          {/* Alert Dialog */}
          <motion.div 
            className={`relative ${styles.bg} ${styles.border} border shadow-xl rounded-xl w-full max-w-md overflow-hidden`}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
          >
            {/* Auto-close Progress */}
            {autoClose > 0 && showProgress && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200">
                <div 
                  className="h-full bg-blue-500 transition-all duration-200 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
            
            {/* Content */}
            <div className="p-6">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0 mr-4 rtl:mr-0 rtl:ml-4">
                  {customIcon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
                  <p className="text-gray-600 text-sm">{message}</p>
                </div>
              </div>
              
              {/* Buttons */}
              <div className="flex justify-end mt-6 space-x-3 rtl:space-x-reverse">
                {onCancel && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    className={`${styles.cancelBtn} px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors`}
                  >
                    {cancelText}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleConfirm}
                  className={`${styles.confirmBtn} px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors`}
                >
                  {confirmText}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// Simple usage example with hook for managing alert state
export const useAlert = () => {
  const [alertState, setAlertState] = useState<MacOSAlertProps>({
    isOpen: false,
    title: "",
    message: "",
    type: "default",
    onClose: () => {},
    confirmText: "موافق",
    cancelText: "إلغاء",
    autoClose: 0,
    showProgress: true
  });

  const openAlert = (config: Omit<MacOSAlertProps, "isOpen" | "onClose">) => {
    setAlertState({ ...config, isOpen: true, onClose: closeAlert });
  };

  const closeAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  };

  return {
    alertState,
    openAlert,
    closeAlert,
    AlertComponent: (
      <MacOSAlert
        {...alertState}
        onClose={closeAlert}
      />
    )
  };
};

// Global alert context
import { createContext, useContext, ReactNode } from 'react';

type AlertContextType = {
  alert: (options: Omit<MacOSAlertProps, 'isOpen' | 'onClose'>) => void;
};

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { openAlert, AlertComponent } = useAlert();

  const alertFunction = (options: Parameters<AlertContextType['alert']>[0]) => {
    openAlert(options);
  };

  return (
    <AlertContext.Provider value={{ alert: alertFunction }}>
      {children}
      {AlertComponent}
    </AlertContext.Provider>
  );
};

export const useGlobalAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useGlobalAlert must be used within an AlertProvider');
  }
  return context;
};

export default MacOSAlert;